// Who owns this device's localStorage — the localStorage half of cacheInit.
//
// cacheInit (./index.ts) already answers this question for IndexedDB: it records
// the owning EMAIL in appMeta and wipes the whole message cache when a DIFFERENT
// human signs in on the same device. localStorage had no equivalent, and logout
// removes exactly TWO keys from it — `cyborg7-session` (clearSavedSession) and
// `cyborg7-refresh-token` (clearRefreshToken). Every other `clearLocal()` in
// app.svelte.ts is memory-only and says so. So a shared device kept the previous
// account's drafts, queued prompts, board-query answers, terminal pointers and —
// worst — an actionable shell-approval card and a superadmin's own bearer token
// sitting in storage for whoever signed in next.
//
// This is the SAME rule, one layer up, and deliberately not a second idea:
//
//   • keyed on the EMAIL, not `user.id`. One human does NOT have one id here —
//     the cloud relay answers `cyborg:auth` with the PG `users.id` while a
//     daemon-direct / self-hosted login answers with `canonicalUserId(email)`.
//     An id compare reads every cloud↔self-host switch as "a different user" and
//     wipes a cache that was perfectly valid. See cacheInit's note.
//
//   • NO stored owner ⇒ adopt, wipe nothing. Before this existed there was only
//     ever one bucket, and the overwhelmingly common device has exactly one
//     human. Wiping on the first launch after upgrading would be pure data loss
//     for every single-user install to protect the shared-device minority. Same
//     call cacheInit makes.
//
//   • a DIFFERENT owner ⇒ remove the owned keys, then record the new owner.
//
// Runs on LOGIN rather than logout on purpose: a crash, a force-quit or a killed
// tab never runs logout at all, so a logout-only wipe protects exactly the case
// that was never at risk. Login is the one moment the next human is provably
// present — and it is the moment before anything hydrates from storage.
//
// Storage-injected (no live-store imports) so the whole decision runs under
// plain-node vitest, same shape as agent-roster-cache.ts / session-list-cache.ts.

/** Minimal storage surface. Nullable so an SSR/prerender pass is a clean no-op. */
export type LocalStore = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem" | "key" | "length"
> | null;

/** Where the owning email is recorded. Never itself an owned key. */
export const LOCAL_OWNER_KEY = "cyborg7:localOwner";

/**
 * Normalize an email into the owner key. Byte-identical rule to cacheInit's
 * ownerKey and session-list-cache's sessionCacheOwner, so the three guards can
 * never disagree about who a given payload belongs to.
 */
export function localOwner(email: string | null | undefined): string {
  return typeof email === "string" ? email.trim().toLowerCase() : "";
}

/**
 * The viewer segment of every per-person cache key (CYBO-1846): the sign-in wipe's identity
 * rule above, hashed so a key is opaque and never carries an address. Empty when signed out,
 * which callers read as "cache nothing". The ONE place to change what a viewer is.
 */
export function viewerCacheKeySegment(email: string | null | undefined): string {
  const owner = localOwner(email);
  return owner ? `v${cyrb53(owner)}` : "";
}

/**
 * A per-person localStorage value written WITH its viewer (CYBO-1846 S1): a second tab still
 * signed in as the previous person can write after this device was claimed, so the key alone
 * proves nothing about whose value it holds. Null when signed out: nothing is written.
 */
export function sealOwned(email: string | null | undefined, value: string): string | undefined {
  const owner = viewerCacheKeySegment(email);
  return owner ? JSON.stringify({ owner, value }) : undefined;
}

/** The value sealOwned wrote for this viewer; undefined (a miss) for another person's, a legacy plain value or garbage. */
export function openOwned(
  email: string | null | undefined,
  raw: string | null,
): string | undefined {
  const owner = viewerCacheKeySegment(email);
  if (!owner || raw === null) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return undefined;
    const { owner: wrote, value } = parsed as { owner?: unknown; value?: unknown };
    return wrote === owner && typeof value === "string" ? value : undefined;
  } catch {
    // intentional: a legacy plain value is not JSON; a miss is the answer, not a failure.
    return undefined;
  }
}

/** Is this a segment viewerCacheKeySegment builds? Tells a viewer-keyed key from a legacy one. */
export function isViewerCacheKeySegment(segment: string): boolean {
  return /^v[0-9a-z]+$/.test(segment);
}

// ponytail: cyrb53, a 53-bit non-crypto hash. A collision only shares keys between two
// people the email-keyed sign-in wipe still separates; use a digest if keys leave the device.
function cyrb53(text: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/**
 * Key PREFIXES whose entries hold one account's data under a per-workspace /
 * per-agent / per-project suffix. Matched with startsWith.
 *
 * Every prefix here ends in its own separator so it cannot swallow a sibling
 * namespace — `cyborg7:draft:` does not match `cyborg7:draft-ts:`, which is why
 * both are listed.
 */
const OWNED_PREFIXES: readonly string[] = [
  // An actionable approval card — a shell command + absolute paths, restored as
  // a clickable Allow/Deny control. The headline of this whole sweep.
  "cyborg7.pendingPerms.",
  // Prompts an older app parked behind a running turn: sent once by legacy-queue-migration, or
  // wiped here when someone else signs in (the app keeps no queue since CYBORG-1560).
  "cyborg7.queuedPrompts.",
  // Verbatim unsent composer text (channel / DM / thread) and its edit clock.
  "cyborg7:draft:",
  "cyborg7:draft-ts:",
  // A finished board analysis AND a pointer to a still-LIVE ephemeral session
  // that "Resume last analysis" reattaches to.
  "cyborg7:board-query-last:",
  "cyborg7:board-query-history:",
  // Cold-start daemon caches: agent rows and archived-session titles + cwds.
  "cyborg7:agentRoster:",
  "cyborg7:sessionList:",
  // Which chat this account had open last — auto-restored on launch, so it
  // navigates the next human straight into the previous one's last DM peer.
  "cyborg7:lastChat:",
];

/**
 * EXACT keys. Deliberately not prefixes: `cyborg7_favorites` is a strict prefix
 * of the correctly-scoped `cyborg7_favorites_<userId>`, and prefix-matching it
 * would delete the very per-user namespaces that exist so a shared device does
 * NOT lose both humans' data on every switch.
 */
const OWNED_KEYS: ReadonlySet<string> = new Set([
  // A superadmin's OWN url+token, left behind by the removed impersonation feature
  // (CYBORG-1102). Browsers that used it still hold the pass, so it is swept here
  // and at logout (clearSavedSession) rather than waiting for it to expire.
  "cyborg7-superadmin-impersonation",
  // Live pty pointers + cwd basenames, and the ids dismissed from the sidebar.
  "cyborg7_terminal_sessions",
  "cyborg7_terminal_dismissed",
  // User-typed terminal rename labels. Server-backed PER USER; the localStorage
  // mirror is the offline/solo fallback and was the only global copy.
  "cyborg7-terminal-aliases",
  // Which workspace this account was last in.
  "cyborg7:lastWorkspace",
  // "hide from my sidebar" — the module's own comment calls it personal.
  "cyborg7_dismissed_work_sessions",
  // Free-typed status text ("At the doctor"), offered as suggestions.
  "cyborg7_recent_statuses",
  // The retired Daily brief's user-authored LLM prompt: residue an older app left behind.
  "cyborg7:tasks-daily-brief-prompt",
  // ─── Legacy residue of the eight `<base>_<userId>` namespaces ──────────────
  // Their key getters fall back to the UNSUFFIXED base when no user is bound, so
  // a persist() after clearLocal() (logout unbinds but leaves the store live)
  // landed the departing account's data in a shared global bucket. Nothing ever
  // READS these — every read sits inside bindUser, which binds first — so this
  // is residue rather than a live read path, and the getters refuse to write
  // while unbound now. Listed so blobs written by earlier builds don't linger.
  "cyborg7_user_status",
  "cyborg7_notification_counts",
  "cyborg7_unread_flags",
  "cyborg7_dm_activity",
  "cyborg7_favorites",
  "cyborg7_opened_dms",
  "cyborg7_activity_read",
  "cyborg7_notify_client_prefs",
]);

/**
 * Does this key hold one account's data?
 *
 * Everything NOT matched is left alone, and that is the deliberate default. It
 * covers the genuine DEVICE preferences (theme, sidebar widths, task view
 * options, model favorites/recents, emoji frequency), the already-scoped
 * `<base>_<userId>` namespaces, and `cyborg7_prev_user_id_<serverUrl>` — the
 * anchor migrateRekeyedIdentity needs to recognise a re-key at all, which a
 * wipe would destroy.
 *
 * ponytail: an explicit list, not a pattern. A key added later is not covered
 * until it is added here — same exposure as today, so no regression, and the
 * alternative (wipe-everything-unrecognised) would eat device prefs. If this
 * list starts drifting, the upgrade path is a shared `ownedKey()` helper the
 * writers call, not cleverer matching here.
 */
export function isOwnedKey(key: string): boolean {
  return OWNED_KEYS.has(key) || OWNED_PREFIXES.some((p) => key.startsWith(p));
}

/** What claimLocalStorage did — returned so callers and tests can assert it. */
export type ClaimOutcome = "skipped" | "adopted" | "kept" | "wiped";

/**
 * Record `email` as this device's localStorage owner, removing the previous
 * account's entries first when the owner actually CHANGED.
 *
 * Returns "skipped" (no store / no usable email — never adopt a blank owner),
 * "adopted" (first run: recorded, nothing removed), "kept" (same human) or
 * "wiped" (a different human: owned keys removed).
 */
export function claimLocalStorage(
  store: LocalStore,
  email: string | null | undefined,
): ClaimOutcome {
  const owner = localOwner(email);
  if (!store || !owner) return "skipped";
  try {
    const previous = store.getItem(LOCAL_OWNER_KEY);
    if (previous === owner) return "kept";
    if (previous) removeOwnedKeys(store);
    store.setItem(LOCAL_OWNER_KEY, owner);
    return previous ? "wiped" : "adopted";
  } catch {
    // intentional: storage blocked/full. Returning "skipped" leaves the device on
    // the status quo — the same best-effort contract cacheInit has.
    return "skipped";
  }
}

function removeOwnedKeys(store: NonNullable<LocalStore>): void {
  // Snapshot first: Storage indices shift as entries are deleted.
  const keys = Array.from({ length: store.length }, (_, i) => store.key(i));
  for (const key of keys) {
    if (key && isOwnedKey(key)) store.removeItem(key);
  }
}

/** Account deleted: owner is keyed by email, so a same-address re-signup would inherit these. */
export function releaseLocalStorage(store: LocalStore): void {
  if (!store) return;
  try {
    removeOwnedKeys(store);
    store.removeItem(LOCAL_OWNER_KEY);
  } catch {
    // intentional: storage blocked; same best-effort contract as claimLocalStorage.
  }
}
